/**
 * Magento-parity PDP ATC label states (mirrors PLP card):
 * idle → adding → added → idle
 *
 * Idle / adding include price: "$20 | add to bag" / "$20 | adding..."
 * Added is plain: "added"
 *
 * On added: open cart drawer (same as PLP) + show page success banner.
 * cart-drawer.js skips its own auto-open for this component to avoid races.
 */

import { StandardEvents } from '@shopify/events';

const ADDED_RESET_MS = 1600;

class MgPdpAddToBag {
  /** @type {WeakMap<HTMLElement, number>} */
  static #resetTimers = new WeakMap();

  /** @param {HTMLElement} host */
  static init(host) {
    if (!(host instanceof HTMLElement) || host.dataset.mgPdpAtcBound === 'true') return;
    host.dataset.mgPdpAtcBound = 'true';

    const form = host.closest('product-form-component');
    if (!form) return;

    const button = host.querySelector('[data-mg-pdp-atc-button]');
    if (!(button instanceof HTMLButtonElement)) return;

    form.addEventListener('submit', () => {
      if (button.disabled) return;
      MgPdpAddToBag.#setState(host, 'adding');
    });

    form.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      if (event.currentTarget !== form && event.target !== form) return;
      const cartEvent = /** @type {{ promise?: Promise<{ detail?: { didError?: boolean } }> }} */ (event);
      if (cartEvent.promise instanceof Promise) {
        cartEvent.promise
          .then((result) => {
            if (result?.detail?.didError) {
              MgPdpAddToBag.#setState(host, 'idle');
              return;
            }
            MgPdpAddToBag.#setState(host, 'added');
          })
          .catch(() => MgPdpAddToBag.#setState(host, 'idle'));
        return;
      }
      MgPdpAddToBag.#setState(host, 'added');
    });

    form.addEventListener(StandardEvents.cartError, (event) => {
      if (event.currentTarget !== form && event.target !== form) return;
      MgPdpAddToBag.#setState(host, 'idle');
    });
  }

  /** Open cart drawer once the cart update has settled. */
  static #openCartDrawer() {
    /** @type {HTMLElement & { open?: () => void; isOpen?: boolean } | null} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer || typeof drawer.open !== 'function' || drawer.isOpen) return;
    drawer.open();
  }

  /**
   * Magento `.message-success`: “You added {title}. to your shopping cart.”
   * @param {HTMLElement} host
   * @returns {boolean}
   */
  static #showCartMessage(host) {
    const banner = document.getElementById('mg-pdp-cart-message');
    if (!(banner instanceof HTMLElement)) return false;

    const textEl = banner.querySelector('.mg-pdp-cart-message__text');
    if (!(textEl instanceof HTMLElement)) return false;

    let title = (host.dataset.productTitle || '').trim();
    if (!title) {
      const heading = document.querySelector('.product-information h1, product-component h1, h1');
      title = (heading?.textContent || '').trim();
    }
    if (!title) return false;

    title = title.toLowerCase();
    if (!title.endsWith('.')) title = `${title}.`;

    const template =
      banner.dataset.messageTemplate || 'You added [[product_title]] to your [[cart_link]].';
    const cartUrl = banner.dataset.cartUrl || '/cart';
    const cartLabel = banner.dataset.cartLinkLabel || 'shopping cart';

    const link = document.createElement('a');
    link.href = cartUrl;
    link.textContent = cartLabel;

    const withTitle = template.replaceAll('[[product_title]]', title);
    const parts = withTitle.split('[[cart_link]]');

    textEl.replaceChildren();
    if (parts.length === 1) {
      textEl.textContent = withTitle;
    } else {
      textEl.append(parts[0], link, parts.slice(1).join('[[cart_link]]'));
    }

    banner.hidden = false;

    banner.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    return true;
  }

  /**
   * @param {HTMLElement} host
   * @param {'idle' | 'adding' | 'added'} state
   */
  static #setState(host, state) {
    const prev = MgPdpAddToBag.#resetTimers.get(host);
    if (prev) clearTimeout(prev);

    if (state === 'idle') {
      host.removeAttribute('data-mg-state');
    } else {
      host.setAttribute('data-mg-state', state);
    }

    host.querySelectorAll('.mg-pdp-add-to-bag__label').forEach((label) => {
      if (!(label instanceof HTMLElement)) return;
      const active = label.classList.contains(`mg-pdp-add-to-bag__label--${state === 'idle' ? 'idle' : state}`);
      label.setAttribute('aria-hidden', active ? 'false' : 'true');
    });

    const button = host.querySelector('[data-mg-pdp-atc-button]');
    if (button instanceof HTMLButtonElement) {
      if (state === 'adding' || state === 'added') {
        button.setAttribute('aria-busy', 'true');
      } else {
        button.removeAttribute('aria-busy');
      }
    }

    if (state === 'added') {
      MgPdpAddToBag.#openCartDrawer();
      MgPdpAddToBag.#showCartMessage(host);

      const timer = window.setTimeout(() => {
        MgPdpAddToBag.#setState(host, 'idle');
      }, ADDED_RESET_MS);
      MgPdpAddToBag.#resetTimers.set(host, timer);
    }
  }
}

function bindAll() {
  document.querySelectorAll('add-to-cart-component[data-mg-pdp-atc="true"]').forEach((host) => {
    if (host instanceof HTMLElement) MgPdpAddToBag.init(host);
  });
}

bindAll();

document.addEventListener('shopify:section:load', bindAll);
document.addEventListener('shopify:block:select', bindAll);

const observer = new MutationObserver(() => bindAll());
observer.observe(document.documentElement, { childList: true, subtree: true });
